<template>
    <div class="text-slate-800">
        <div
            class="bg-white p-6 flex flex-col gap-4 border-b border-x-0 border-t-0 border-b-slate-100 border-solid">

            <div>
                <div class="text-2xl font-extrabold">
                    <slot name="header">Select Items</slot>
                </div>
                <div class="text-sm text-slate-500">
                    <slot name="subheader">You can select multiple items.</slot>
                </div>
            </div>

            <div>
                <vx-tabs v-model="tab"
                    size="lg">
                    <vx-tab value="all">All</vx-tab>
                    <vx-tab value="selected">Selected ({{ selected.length }})</vx-tab>
                </vx-tabs>
            </div>
            <div>
                <vx-input ref="searchEl"
                    v-model="search"
                    @keydown.enter="handleEnter"
                    size="lg"
                    data-test="search-input">
                    <template v-slot:right>
                        <button
                            v-if="hasSearchText"
                            @click="clearSearch"
                            type="button"
                            aria-label="Clear search"
                            class="cursor-pointer"
                            data-test="clear-search-button"
                        >
                            <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                        </button>
                        <font-awesome-icon v-else widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <div class="bg-slate p-6 lg:px-12 lg:py-10">
            <div v-if="props.showSelectAll && (tab === 'selected' ? filteredSelected.length > 0 : filteredOptions.length > 0)" class="flex justify-between items-center pl-3 sm:pl-4 pb-2">
                <div
                    @click="tab === 'selected'? deselectAll() : toggleAll()"
                    role="button"
                    tabindex="0"
                    data-test="select-all-button"
                    class="flex items-center gap-2 cursor-pointer ml-px">
                        <vx-checkbox :modelValue="tab == 'selected' ? true :selectAll"></vx-checkbox>
                        <span class="text-sm whitespace-nowrap">
                            {{tab=="selected" ? "Deselect All" : "Select All"}}
                        </span>
                </div>

                <slot name="select-all-right"/>
            </div>

            <slot name="before-items"/>

            <div v-if="tab === 'all'">

                <div v-if="filteredOptions.length > 0"
                    class="flex flex-col gap-2">
                    <page-select-item v-for="item in filteredOptions"
                        :key="'all_' + props.getOptionKey(item)"
                        :label="props.getOptionLabel(item)"
                        @toggle="toggle(item)"
                        :selected="isSelected(item)"
                        :disabled="item.disabled">
                        <slot name="option"
                            :item="item"
                            :selected="isSelected(item)"></slot>
                    </page-select-item>
                </div>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <template v-if="props.loading">
                        <vx-icon-loading spin
                            class="w-6 h-6"></vx-icon-loading>
                        <span class="text-lg">Loading...</span>
                    </template>
                    <template v-else>
                        <font-awesome-icon widthAuto :icon="faSquareDashed"
                            class="text-2xl"></font-awesome-icon>
                        <span class="text-lg">No results found</span>
                    </template>
                </div>
            </div>

            <div v-else-if="tab === 'selected'">
                <div v-if="filteredSelected.length > 0"
                    class="flex flex-col gap-2">
                    <page-select-item v-for="selectedItem in filteredSelected"
                        :key="'selected_' + props.getOptionKey(selectedItem)"
                        :label="props.getOptionLabel(selectedItem)"
                        @toggle="toggle(selectedItem)"
                        :selected="true"
                        :disabled="selectedItem.disabled">
                        <slot name="option"
                            :item="selectedItem"
                            :selected="true"></slot>
                    </page-select-item>
                </div>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <font-awesome-icon widthAuto :icon="faSquareDashed"
                        class="text-2xl"></font-awesome-icon>
                    <span class="text-lg">
                        {{ search ? 'No results found' : 'Nothing selected' }}
                    </span>
                </div>
            </div>

        </div>
    </div>
</template>


<script setup>
import { faMagnifyingGlass, faSquareDashed, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxCheckbox, VxIconLoading, VxInput, VxTab, VxTabs } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageSelectItem from '~/components/general/PageSelectItem.vue'

const selected = defineModel({
    type: Array,
    required: true,
});

const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    loading: {
        type: Boolean
    },
    getOptionKey: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.id || option.value
        },
    },
    getOptionLabel: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.name || option.label
        },
    },
    customSearch: {
        type: Function,
        default: null,
    },
    showSelectAll: {
        type: Boolean,
        default: true,
    },
});

const searchEl = ref()
const tab = ref('all')
const search = ref('')

const hasSearchText = computed(() => search.value.trim().length > 0)

const clearSearch = () => {
    search.value = ''
    nextTick().then(() => searchEl.value?.field?.focus?.())
}

watch(tab, () => {
    if (tab.value === 'selected') {
        search.value = '';
    }
    nextTick().then(() => searchEl.value?.field?.focus?.())
}, {
    immediate: true
});

watch(search, (search) => {
    emit('search', search);
})

const emit = defineEmits(['search', 'option:selected', 'option:deselected']);

const searchList = (list) => {
    const searchFor = search.value.trim()
    if (!searchFor.length) {
        return list;
    }

    return list.filter(option => {
        if (props.customSearch) {
            return props.customSearch(option, searchFor);
        }

        const searchForLower = searchFor.toLowerCase();
        const key = String(props.getOptionKey(option) || '').toLowerCase();
        const label = String(props.getOptionLabel(option) || '').toLowerCase();

        return key.includes(searchForLower) || label.includes(searchForLower);
    })
}

const filteredOptions = computed(() => searchList(props.options))
const filteredSelected = computed(() => searchList(selected.value))

const isSelected = (optionToCheck) => {
    return selected.value.some(option => props.getOptionKey(optionToCheck) == props.getOptionKey(option))
}

const toggle = (optionToToggle) => {
    const existingIndex = selected.value.findIndex(option => props.getOptionKey(optionToToggle) == props.getOptionKey(option))
    if (existingIndex !== -1) {
        selected.value.splice(existingIndex, 1)
        emit('option:deselected', optionToToggle)
    } else {
        selected.value.push(optionToToggle);
        emit('option:selected', optionToToggle)
    }
}

const selectAll = computed(() => {
    if (filteredOptions.value.length === 0) {
        return false;
    }
    return filteredOptions.value.every(isSelected)
})

const deselectAll = () => {
    selected.value.forEach(selectedOption => {
        emit('option:deselected', selectedOption);
    })
    selected.value = []
}

const toggleAll = () => {
    if (selectAll.value) {
        deselectAll();
    } else {
        const allOptions = filteredOptions.value;
        const newSelections = allOptions.filter(option => !isSelected(option));
        newSelections.forEach(option => {
            emit('option:selected', option)
        })
        selected.value = [...selected.value, ...newSelections];
    }
}

const handleEnter = () => {
    if (!search.value.length) {
        return;
    }

    if (props.loading) {
        return;
    }

    if (filteredOptions.value[0]) {
        toggle(filteredOptions.value[0]);
    }
};
</script>
